import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import type { TooltipPayloadEntry } from "recharts";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

import type { ChartConfig } from "~/components/ui/chart";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "~/components/ui/chart";
import { StoreRegion, useStatsHistogramQuery } from "~/graphql/graphql.gen";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { formatPrice } from "~/utils/currency";
import { formatDate } from "~/utils/date";
import { SkeletonAreaChart } from "~/views/analytics/skeleton-area-chart";

const chartConfig = {
  amount: {
    label: "Amount",
    color: "#13482B",
  },
} satisfies ChartConfig;

function SalesByDayAreaChart() {
  const { i18n, t } = useTranslation();

  const activeSale = useAtomValue(activeSaleState);
  const brand = useAtomValue(brandState);

  const { data: statsHistogramData } = useStatsHistogramQuery(
    {
      saleId: activeSale?.id ?? "",
    },
    {
      enabled: !!activeSale?.id,
      select: (data) =>
        data.statsHistogram.map((statHistogram) => ({
          date: new Date(statHistogram.date),
          sum: statHistogram.nb_distinct_orders ?? 0,
          amount:
            ((brand?.store === StoreRegion.Fr
              ? statHistogram.total_operation_ttc
              : statHistogram.total_operation_ht) ?? 0) / 100,
        })),
      refetchOnWindowFocus: false,
    },
  );

  return (statsHistogramData ?? []).length ? (
    <ChartContainer config={chartConfig} className="min-h-[400px] w-full pb-4">
      <AreaChart
        accessibilityLayer
        data={statsHistogramData ?? []}
        margin={{
          left: 2,
          right: 12,
        }}
      >
        <defs>
          <linearGradient id="fillDesktop" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="rgba(19, 72, 43, 0.8)" />
            <stop offset="95%" stopColor="rgba(19, 72, 43, 0.1)" />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} horizontal={false} />
        <XAxis
          dataKey="date"
          tickLine={true}
          axisLine={true}
          stroke="#D8D9DA"
          tickMargin={8}
          tick={{
            fill: "#8A8D8F",
          }}
          tickFormatter={(value: Date) =>
            formatDate(value, i18n.language.replace("_", "-"), {
              day: "numeric",
              month: "short",
            })
          }
        />
        <YAxis
          tickLine={true}
          axisLine={true}
          stroke="#D8D9DA"
          tickMargin={8}
          tick={{
            fill: "#8A8D8F",
          }}
          tickFormatter={(value: number) =>
            formatPrice(
              value,
              i18n.language.replace("_", "-"),
              activeSale?.currency.toString().toUpperCase() ?? "EUR",
              {
                minimumFractionDigits: 0,
                notation: "compact",
              },
            )
          }
        />
        <ChartTooltip
          cursor={{
            stroke: "#13482B",
            strokeWidth: 2,
          }}
          content={
            <ChartTooltipContent
              indicator="dot"
              hideLabel
              className="bg-gray-900 text-gray-100"
              formatter={(_value, _name, item: TooltipPayloadEntry) => {
                const payload = item.payload as NonNullable<
                  typeof statsHistogramData
                >[number];
                return (
                  <div className="grid min-w-[130px] gap-2 p-2 text-xs">
                    <span className="font-semibold uppercase">
                      {formatDate(
                        payload.date,
                        i18n.language.replace("_", "-"),
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        },
                      )}
                    </span>
                    <div>
                      {t("sales_by_day_area_chart.sales")}{" "}
                      <b>
                        {formatPrice(
                          payload.amount,
                          i18n.language.replace("_", "-"),
                          activeSale?.currency.toString().toUpperCase() ??
                            "EUR",
                          {
                            minimumFractionDigits: 0,
                          },
                        )}
                      </b>
                    </div>
                    <div>
                      {t("sales_by_day_area_chart.orders")} <b>{payload.sum}</b>
                    </div>
                  </div>
                );
              }}
            />
          }
        />
        <Area
          dataKey="amount"
          type="linear"
          fill="url(#fillDesktop)"
          fillOpacity={1}
          stroke="#13482B"
          strokeWidth={2}
          dot={{
            r: 3.5,
            fill: "#13482B",
          }}
          activeDot={({ cx, cy }) => {
            if (cx == null || cy == null) {
              return null;
            }
            const r = 8.5;
            return (
              <svg
                x={cx - r}
                y={cy - r}
                width={r * 2}
                height={r * 2}
                viewBox="0 0 17 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx={r} cy={r} r="5" fill="#13482B" stroke="#13482B" />
                <circle cx={r} cy={r} r="5.5" stroke="white" strokeWidth={2} />
                <circle
                  cx={r}
                  cy={r}
                  r="7.5"
                  stroke="#13482B"
                  strokeWidth={2}
                />
              </svg>
            );
          }}
        />
      </AreaChart>
    </ChartContainer>
  ) : (
    <SkeletonAreaChart />
  );
}

export { SalesByDayAreaChart };
